/**
 * The RBAC catalogue and the client-side permission matcher.
 *
 * Lives in the leaf layer, not in `features/access`, so any feature can gate its
 * UI on a permission (`app -> features -> leaves`, see eslint's
 * `import/no-restricted-paths`).
 */

export interface GrantedPermission {
  resource: string;
  action: string;
}

/**
 * One Grant as the user listing previews it, without the permissions behind it
 * (admin-api `RoleGrantSummaryResponse`) — a row of `GET /v1/users` must not pay
 * for a permission preload.
 *
 * `id` is the **Role's** id, not the assignment's, so it is what goes back as the
 * `role_id` of a grant or a revoke.
 */
export interface RoleGrantSummary {
  id: string;
  name: string;
  description: string;
  /** '*' for a wildcard scope, matching the backend's stored scope exactly. */
  game_code: string;
  sandbox_code: string;
}

export interface MyRoleGrant extends RoleGrantSummary {
  permissions: GrantedPermission[];
}

// Mirrors the admin-api seed migrations (20260702134918_seed_rbac_catalog.sql,
// 20260901083305_add_import_permissions.sql,
// 20260907142107_split_transaction_permissions.sql,
// 20260909171737_rename_admin_resource_and_add_roles_read.sql,
// 20260910070615_add_sandboxes_export_permission.sql). Wildcards exist only on
// the Grant side — the UI always asks for a concrete permission.
//
// `reserved` marks a code that governs no endpoint today: granting it changes
// nothing, so never gate a control on one. They stay in the catalogue because
// `expandWildcards` reads it to say what a '*' Grant covers.
const StorePermission = {
  read: 'store:read',
  create: 'store:create',
  edit: 'store:edit',
  delete: 'store:delete',
  publish: 'store:publish',
  /** Not implied by create (BR-RBAC-36). */
  import: 'store:import',
} as const;

const ItemsPermission = {
  read: 'items:read',
  create: 'items:create',
  edit: 'items:edit',
  /** Reserved. */
  delete: 'items:delete',
  /** Also how bundles and currencies are imported (BR-RBAC-34); not implied by create. */
  import: 'items:import',
} as const;

/** Reserved in full — no bundle endpoint exists today. */
const BundlesPermission = {
  read: 'bundles:read',
  create: 'bundles:create',
  edit: 'bundles:edit',
  delete: 'bundles:delete',
} as const;

const GiftsPermission = {
  read: 'gifts:read',
  create: 'gifts:create',
  edit: 'gifts:edit',
  delete: 'gifts:delete',
  /** Not implied by create (BR-RBAC-36). */
  import: 'gifts:import',
} as const;

const SeasonPassPermission = {
  read: 'seasonpass:read',
  create: 'seasonpass:create',
  edit: 'seasonpass:edit',
  delete: 'seasonpass:delete',
  /** Not implied by create (BR-RBAC-36). */
  import: 'seasonpass:import',
} as const;

/** Game-wide: check these with scope 'game', not 'sandbox'. */
const BansPermission = {
  /** Reserved. */
  read: 'bans:read',
  create: 'bans:create',
  edit: 'bans:edit',
  /** Reserved. */
  delete: 'bans:delete',
} as const;

const SandboxesPermission = {
  read: 'sandboxes:read',
  /** Game-level: whoever can create a Sandbox can create them anywhere in the Game. */
  create: 'sandboxes:create',
  edit: 'sandboxes:edit',
  delete: 'sandboxes:delete',
  publish: 'sandboxes:publish',
  /** Not implied by read — the export dumps the whole catalogue (BR-RBAC-42). */
  export: 'sandboxes:export',
} as const;

const TranslationsPermission = {
  /** Reserved — reading translations needs items:read, and languages:read for the registry. */
  read: 'translations:read',
  edit: 'translations:edit',
} as const;

const LanguagesPermission = {
  read: 'languages:read',
  edit: 'languages:edit',
} as const;

const InventoryPermission = {
  read: 'inventory:read',
  grant: 'inventory:grant',
} as const;

const InventoryTransactionsPermission = {
  read: 'inventory_transactions:read',
} as const;

const StoreTransactionsPermission = {
  read: 'store_transactions:read',
} as const;

const PlayersPermission = { read: 'players:read' } as const;

/**
 * Checked at Game scope by admin-api, so a Grant on any one Sandbox of the Game
 * satisfies it. It authorizes the caller and does not narrow the answer: the
 * directory it opens stays Nacon-wide, and a member's Grants come back whatever
 * Game they bear on.
 *
 * `manage_permissions` lives here too — what it manages is a user's Roles, the
 * same subject `read` names. It was `admin:manage_permissions` until the catalogue
 * was renamed.
 */
const UsersPermission = {
  read: 'users:read',
  managePermissions: 'users:manage_permissions',
} as const;

/** Checked at Game scope by admin-api: the catalogue is read to attribute a Grant. */
const RolesPermission = { read: 'roles:read' } as const;

/** Bundled by no standard Role — only `administrator` holds it. */
const BeaconsPermission = { send: 'beacons:send' } as const;

/** The single entry point for the codes: `Permissions.players.read`. */
export const Permissions = {
  bans: BansPermission,
  beacons: BeaconsPermission,
  bundles: BundlesPermission,
  gifts: GiftsPermission,
  inventory: InventoryPermission,
  inventory_transactions: InventoryTransactionsPermission,
  items: ItemsPermission,
  languages: LanguagesPermission,
  players: PlayersPermission,
  roles: RolesPermission,
  sandboxes: SandboxesPermission,
  seasonpass: SeasonPassPermission,
  store: StorePermission,
  store_transactions: StoreTransactionsPermission,
  translations: TranslationsPermission,
  users: UsersPermission,
} as const;

type Values<T> = T[keyof T];

export type PermissionCode = Values<{
  [Resource in keyof typeof Permissions]: Values<
    (typeof Permissions)[Resource]
  >;
}>;

export const WILDCARD = '*';

/** A '*' on the granted side widens it; an omitted request leaves it unconstrained. */
const covers = (granted: string, requested?: string) =>
  requested === undefined || granted === WILDCARD || granted === requested;

const grantHolds = (
  grant: MyRoleGrant,
  { resource, action }: Partial<GrantedPermission>,
  gameCode?: string,
  sandboxCode?: string,
) =>
  covers(grant.game_code, gameCode) &&
  covers(grant.sandbox_code, sandboxCode) &&
  grant.permissions.some(
    (permission) =>
      covers(permission.resource, resource) &&
      covers(permission.action, action),
  );

/**
 * Whether one Grant satisfies `code` in the given scope, matching wildcards the way
 * the backend does: `game_code IN (requested, '*') AND sandbox_code IN (requested, '*')`.
 * An omitted scope segment is unconstrained — a page with no :sandboxCode accepts a
 * Grant scoped to any single Sandbox.
 */
export const grantCovers = (
  grant: MyRoleGrant,
  code: PermissionCode,
  gameCode?: string,
  sandboxCode?: string,
) => {
  const [resource, action] = code.split(':');

  return grantHolds(grant, { resource, action }, gameCode, sandboxCode);
};

/**
 * Whether `grants` hold every one of `permissions` on (gameCode, sandboxCode),
 * each from whichever Grant carries it — admin-api's condition for granting a Role
 * that confers them. A '*' asked for is only covered by a '*' held: `store:*`
 * needs `store:*` or `*:*`, not every store action one by one.
 */
export const holdsAll = (
  grants: MyRoleGrant[],
  permissions: GrantedPermission[],
  gameCode: string,
  sandboxCode: string,
) =>
  permissions.every((permission) =>
    grants.some((grant) =>
      grantHolds(grant, permission, gameCode, sandboxCode),
    ),
  );

const actionsOf = (resource: string) =>
  resource in Permissions
    ? Object.values(Permissions[resource as keyof typeof Permissions]).map(
        (code) => code.split(':')[1] ?? code,
      )
    : [];

/**
 * Replaces wildcards with the concrete permissions they cover, for display: '*:*'
 * becomes every permission in the catalogue, 'store:*' every store action. A resource
 * the catalogue doesn't know passes through untouched, so a permission the backend
 * adds still shows up.
 */
export const expandWildcards = (
  permissions: GrantedPermission[],
): GrantedPermission[] => {
  const expanded = permissions.flatMap(({ resource, action }) =>
    (resource === WILDCARD ? Object.keys(Permissions) : [resource]).flatMap(
      (expandedResource) => {
        if (action !== WILDCARD)
          return [{ resource: expandedResource, action }];
        const actions = actionsOf(expandedResource);
        return actions.length > 0
          ? actions.map((expandedAction) => ({
              resource: expandedResource,
              action: expandedAction,
            }))
          : [{ resource: expandedResource, action }];
      },
    ),
  );

  // A Role can hold both 'store:*' and 'store:read'; show each permission once.
  return [
    ...new Map(
      expanded.map((permission) => [
        `${permission.resource}:${permission.action}`,
        permission,
      ]),
    ).values(),
  ];
};

/**
 * Whether any Grant covers this (Game, Sandbox) at all, whatever it permits — "may
 * this user enter this Sandbox", which no single code answers: a Role scoped to one
 * Sandbox opens it whatever its permissions, and `sandboxes:read` is held by almost
 * no Standard Role.
 */
export const hasGrantOnScope = (
  grants: MyRoleGrant[],
  gameCode?: string,
  sandboxCode?: string,
) =>
  grants.some(
    (grant) =>
      covers(grant.game_code, gameCode) &&
      covers(grant.sandbox_code, sandboxCode),
  );

export const hasPermission = (
  grants: MyRoleGrant[],
  code: PermissionCode,
  gameCode?: string,
  sandboxCode?: string,
) => grants.some((grant) => grantCovers(grant, code, gameCode, sandboxCode));

/**
 * The three scope levels of the RBAC Design Document. Most capabilities are checked
 * on the current Game *and* Sandbox; a few are Game-level whatever the Sandbox
 * (`bans:create`, `bans:edit`, `sandboxes:create`, `users:read`, `roles:read`).
 *
 * No endpoint is checked globally any more: the Role writes and the audit log that
 * were the only ones went with the frozen catalogue. 'global' is kept because it is
 * the only way to ask for the ('*', '*') Grant the platform admins hold.
 */
export type PermissionScope = 'sandbox' | 'game' | 'global';

/** Holding any one of `oneOf` passes. Required, so an empty check cannot exist. */
export interface PermissionCheckOptions {
  oneOf: PermissionCode[];
  /**
   * Defaults to 'sandbox'. Note that 'sandbox' on a route without a :sandboxCode
   * degrades to Game level — that is what keeps the Access area open to
   * Sandbox-scoped permission admins.
   */
  scope?: PermissionScope;
  /** Overrides the route's :gameCode. Ignored when scope is 'global'. */
  gameCode?: string;
  /** Overrides the route's :sandboxCode. Only used when scope is 'sandbox'. */
  sandboxCode?: string;
}

/**
 * Whether `grants` satisfy `check` on the scope `params` describe. The single
 * evaluation shared by the component gate (`usePermissionCheck`) and the route gate
 * (`requirePermissions`), so the sidebar and the loaders cannot disagree.
 */
export const holdsPermissions = (
  grants: MyRoleGrant[],
  { oneOf, scope = 'sandbox', gameCode, sandboxCode }: PermissionCheckOptions,
  params: { gameCode?: string; sandboxCode?: string } = {},
): boolean => {
  // 'global' asks for the (*, *) Grant the platform admins hold: '*' on the requested
  // side only matches a '*' Grant. 'game' leaves the Sandbox unconstrained, so a Grant
  // on any single Sandbox of the Game satisfies it.
  const game = scope === 'global' ? WILDCARD : (gameCode ?? params.gameCode);
  const sandbox =
    scope === 'global'
      ? WILDCARD
      : scope === 'game'
        ? undefined
        : (sandboxCode ?? params.sandboxCode);

  return oneOf.some((code) => hasPermission(grants, code, game, sandbox));
};
